import { useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { TopBarV2 } from "./TopBarV2";
import { useBrokerOfferNotifications } from "@/hooks/useBrokerOfferNotifications";
import { useCarrierOfferNotifications } from "@/hooks/useCarrierOfferNotifications";
import { usePrivateLoadAlerts } from "@/hooks/usePrivateLoadAlerts";
import { useGlobalNotificationAlerts } from "@/hooks/useGlobalNotificationAlerts";
import { installAudioUnlock } from "@/lib/audioUnlock";
import { useAuth } from "@/contexts/AuthContext";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import { WebNotificationSoftPrompt } from "@/components/notifications/WebNotificationSoftPrompt";
import { ShipperApprovalGate } from "@/components/dashboard/ShipperApprovalGate";

export function AppShellLayout() {
  // Global in-app alert channel — one toast + one chime per notification INSERT
  // for the logged-in user, regardless of role. Per-role hooks below keep
  // their badge counters + query invalidation but no longer toast/chime.
  useGlobalNotificationAlerts();
  useBrokerOfferNotifications();
  useCarrierOfferNotifications();

  // Prime cached Audio elements on first user gesture (autoplay policy).
  useEffect(() => { installAudioUnlock(); }, []);

  // Broker / broker_carrier: keep this hook mounted for its data-refresh side
  // (badge/count paths). Toast/chime is now owned by useGlobalNotificationAlerts.
  const { user, profile } = useAuth();
  const userType = profile?.user_type;
  const isBroker = userType === "broker" || userType === "broker_carrier";
  usePrivateLoadAlerts(isBroker ? user?.id ?? null : null, isBroker);

  const location = useLocation();

  return (
    <div className="flex h-screen w-full max-w-full min-w-0 flex-col overflow-hidden bg-card">
      <TopBarV2 />
      <main className="flex-1 w-full max-w-full min-w-0 overflow-y-auto overflow-x-hidden overscroll-x-none px-3 py-2 xl:px-4 xl:py-2.5 [&>*]:min-w-0 [&>*]:max-w-full">

        {/* Route-keyed page-level boundary. Any render throw below TopBar is
            caught here so the app never white-screens. Keyed on pathname so
            navigating to a new route clears a prior page's error state. */}
        <ErrorBoundary key={location.pathname} variant="page">
          <Outlet />
        </ErrorBoundary>
      </main>
      {/* One-time soft ask for browser notification permission. */}
      <WebNotificationSoftPrompt />
      {/* Non-dismissible approval gate for new shippers (UI-only for now). */}
      <ShipperApprovalGate />
      {/* VerifyMcGate removed — FMCSA verification is no longer a hard gate. */}
    </div>
  );
}

